import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import SmoothScroll from "@/components/SmoothScroll";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const TOPICS = ["Bespoke Tour", "Logistics & Visa", "Group Travel", "Honeymoon"];

const CONSULT_HERO =
  "https://images.pexels.com/photos/19758828/pexels-photo-19758828.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2400";

export default function Consultations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [topic, setTopic] = useState(TOPICS[0]);
  const [contactMethod, setContactMethod] = useState("whatsapp");
  const [contactValue, setContactValue] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/consultations/slots");
        setSlots(r.data || []);
      } catch {}
    })();
  }, []);

  const days = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const d = s.start_time.split("T")[0];
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(s);
    }
    return Array.from(map.entries()).map(([d, items]) => ({ d, items }));
  }, [slots]);

  const submit = async () => {
    if (!user) return navigate("/login");
    if (!selectedSlot) {
      toast.error("Pick a time slot");
      return;
    }
    if (!contactValue) {
      toast.error("Add a contact");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/consultations", {
        slot_id: selectedSlot.slot_id,
        topic,
        contact_method: contactMethod,
        contact_value: contactValue,
        notes,
      });
      toast.success("Consultation booked. We'll reach out at your scheduled time.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SmoothScroll>
      <div className="min-h-screen bg-[#FDFBF7]">
        <SEO
          title="Consultations — Sketch your Indonesia in 30 minutes"
          description="Book a free 30-minute consultation. Our local concierge sketches a rough route in your time zone — no commitment, no catalogue."
          image={CONSULT_HERO}
        />
        <Navbar transparent />

        <PageHero
          image={CONSULT_HERO}
          eyebrow={t("consult.eyebrow")}
          title={`${t("consult.title_a")} ${t("consult.title_b")}`}
          titleBold={t("consult.title_c")}
          subtitle={t("consult.intro")}
        >
          <div className="flex flex-wrap gap-3">
            <a
              data-testid="quick-whatsapp"
              href="https://wa.me/6281234567890?text=Halo%20Across%20Indonesia"
              target="_blank"
              rel="noreferrer"
              className="btn-pill bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm"
            >
              {t("consult.quick_wa")}
            </a>
            <a
              data-testid="quick-telegram"
              href="https://t.me/acrossindonesia"
              target="_blank"
              rel="noreferrer"
              className="btn-pill bg-sky-50 text-sky-700 hover:bg-sky-100 text-sm"
            >
              {t("consult.quick_tg")}
            </a>
          </div>
        </PageHero>

        <main
          className="max-w-6xl mx-auto px-6 md:px-12 pt-20 pb-24"
          data-testid="consultations-page"
        >
          <div className="grid md:grid-cols-2 gap-12">
            {/* Step 1+2: Choose date & time */}
            <section>
              <div className="t-overline text-slate-500 mb-4">
                {t("consult.step1")}
              </div>
              <div
                className="grid grid-cols-3 sm:grid-cols-4 gap-3"
                data-testid="day-grid"
              >
                {days.slice(0, 12).map((d) => {
                  const date = new Date(d.d);
                  const isSel = selectedDay === d.d;
                  return (
                    <button
                      key={d.d}
                      data-testid={`day-${d.d}`}
                      onClick={() => {
                        setSelectedDay(d.d);
                        setSelectedSlot(null);
                      }}
                      className={`p-4 rounded-2xl text-left transition-colors ${
                        isSel
                          ? "bg-slate-900 text-white"
                          : "bg-white text-slate-800 hover:bg-slate-50 border border-slate-100"
                      }`}
                    >
                      <div className="text-xs uppercase opacity-70">
                        {date.toLocaleDateString("en", { weekday: "short" })}
                      </div>
                      <div className="text-lg font-medium mt-1">{date.getDate()}</div>
                      <div className="text-xs opacity-70">
                        {date.toLocaleDateString("en", { month: "short" })}
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedDay && (
                <div className="mt-8">
                  <div className="t-overline text-slate-500 mb-4">
                    {t("consult.step2")}
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="slot-grid">
                    {days
                      .find((d) => d.d === selectedDay)
                      ?.items.map((s) => {
                        const tt = new Date(s.start_time);
                        const sel = selectedSlot?.slot_id === s.slot_id;
                        return (
                          <button
                            key={s.slot_id}
                            data-testid={`slot-${s.slot_id}`}
                            onClick={() => setSelectedSlot(s)}
                            className={`px-5 py-2 rounded-full text-sm transition-colors ${
                              sel
                                ? "bg-seafoam text-white"
                                : "bg-white text-slate-800 border border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            {tt.toLocaleTimeString("en", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </section>

            {/* Step 3: Form */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100">
              <div className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 mb-4">
                Step 3 · Your details
              </div>

              <label className="block">
                <span className="text-xs uppercase tracking-[0.25em] text-slate-500">
                  Topic
                </span>
                <select
                  data-testid="topic-select"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam"
                >
                  {TOPICS.map((tp) => (
                    <option key={tp}>{tp}</option>
                  ))}
                </select>
              </label>

              <div
                className="mt-5 grid grid-cols-3 gap-2"
                data-testid="contact-method"
              >
                {["whatsapp", "telegram", "email"].map((m) => (
                  <button
                    key={m}
                    data-testid={`method-${m}`}
                    onClick={() => setContactMethod(m)}
                    className={`py-2 rounded-full text-sm capitalize transition-colors ${
                      contactMethod === m
                        ? "bg-slate-900 text-white"
                        : "bg-white border border-slate-200 text-slate-700"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>

              <label className="block mt-5">
                <span className="text-xs uppercase tracking-[0.25em] text-slate-500">
                  {contactMethod === "email"
                    ? "Email"
                    : contactMethod === "telegram"
                    ? "Telegram handle"
                    : "WhatsApp number"}
                </span>
                <input
                  data-testid="contact-value"
                  value={contactValue}
                  onChange={(e) => setContactValue(e.target.value)}
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam"
                />
              </label>

              <label className="block mt-5">
                <span className="text-xs uppercase tracking-[0.25em] text-slate-500">
                  Notes (optional)
                </span>
                <textarea
                  data-testid="consult-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam resize-none"
                />
              </label>

              <button
                data-testid="consult-submit"
                onClick={submit}
                disabled={submitting || !selectedSlot}
                className="mt-6 w-full btn-pill bg-slate-900 text-white hover:bg-slate-800 justify-center disabled:opacity-50"
              >
                {submitting ? "Booking…" : "Confirm consultation"}
              </button>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </SmoothScroll>
  );
}
